/**
 * StoreOrderConfirmPage — Pagina di conferma post-submit ordine.
 * Route: /store/conferma (pubblica, no autenticazione)
 *
 * Riceve i dati dell'ordine via location.state dalla StoreOrderPage.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, Mail, ArrowLeft } from 'lucide-react';

export default function StoreOrderConfirmPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order;
  const ref = localStorage.getItem('asight_channel') || '';

  const withRef = (path) => {
    if (ref) {
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}ref=${encodeURIComponent(ref)}`;
    }
    return path;
  };

  // Se non ci sono dati ordine, redirect al form
  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <p className="text-gray-500 mb-4">Nessun ordine da visualizzare.</p>
        <button
          onClick={() => navigate(withRef('/store/ordine'))}
          className="flex items-center gap-2 text-yellow-600 hover:text-yellow-800 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna al form
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-white via-yellow-100 to-yellow-300 border-b border-yellow-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-yellow-600" />
          <h1 className="text-2xl font-bold text-gray-900">A-Sight Store</h1>
        </div>
      </header>

      {/* Contenuto conferma */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          {/* Icona successo */}
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ordine Ricevuto!
          </h2>

          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {order.messaggio || "Il tuo ordine è stato creato con successo. Controlla la tua email per attivare l'account."}
          </p>

          {/* Dettagli ordine */}
          <div className="bg-gray-50 rounded-xl p-6 text-left max-w-md mx-auto mb-6">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Numero ordine</dt>
                <dd className="font-mono font-bold text-gray-900">{order.numero_ordine}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Stato</dt>
                <dd>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    {order.stato_ordine}
                  </span>
                </dd>
              </div>
              {order.azienda && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Azienda</dt>
                  <dd className="font-medium">{order.azienda.nome_azienda}</dd>
                </div>
              )}
              {order.prodotto && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Prodotto</dt>
                  <dd className="font-medium">{order.prodotto.nome_prodotto}</dd>
                </div>
              )}
              {order.totale_importo && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Totale</dt>
                  <dd className="font-bold text-lg">€{parseFloat(order.totale_importo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</dd>
                </div>
              )}
              {order.channel_type && order.channel_type !== 'DIRETTO' && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Canale</dt>
                  <dd className="font-medium">{order.channel_type}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Istruzioni email */}
          <div className="flex items-center gap-3 bg-yellow-50 rounded-lg p-4 text-left max-w-md mx-auto">
            <Mail className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            <div className="text-sm text-gray-800">
              <p className="font-semibold">Controlla la tua email</p>
              <p className="text-gray-600">
                Riceverai un'email all'indirizzo <strong>{order.azienda?.email}</strong> con il link per attivare il tuo account.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-yellow-200 bg-gradient-to-r from-white via-yellow-50 to-yellow-100 mt-auto">
        <div className="max-w-3xl mx-auto px-4 py-4 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} A-Sight — Focalaize Srl. Tutti i diritti riservati.
        </div>
      </footer>
    </div>
  );
}
